import {
  IndexRouteObject,
  Navigate,
  NonIndexRouteObject,
} from 'react-router-dom';

import {
  BreadcrumbHandle,
  DynamicApplicationListBreadcrumb,
  DynamicInvitationBreadcrumb,
  DynamicTenantBreadcrumb,
} from 'src/components/layout/Breadcrumbs';
import { GLOBAL_ACTIONS, GLOBAL_RESOURCES } from 'src/constants';
import i18n from 'src/i18n';
import { Invitation } from 'src/modules/Invitations/Invitation';
import { InvitationCreate } from 'src/modules/Invitations/InvitationCreate';
import { Invitations } from 'src/modules/Invitations/Invitations';
import { Organizations } from 'src/modules/Organizations';
import {
  ApplicationList,
  Applications as ApplicationListApplications,
  Tenants as ApplicationListTenants,
  Details,
} from 'src/modules/TenantManagement/ApplicationList';
import { ApplicationListCreate } from 'src/modules/TenantManagement/ApplicationListCreate';
import { ApplicationLists } from 'src/modules/TenantManagement/ApplicationLists';
import {
  Members,
  Tenant,
  Details as TenantDetails,
} from 'src/modules/TenantManagement/Tenant';
import { TenantCreate } from 'src/modules/TenantManagement/TenantCreate';
import { Callback } from 'src/router/Callback';
import { ProtectedRoute } from 'src/router/ProtectedRoute';
import { RootBoundary } from 'src/router/RootBoundary';
import { RootLayout } from 'src/router/RootLayout';

interface IndexRouteObjectWithHandle extends IndexRouteObject {
  handle?: BreadcrumbHandle;
}

interface NoIndexRouteObjectWithHandle extends NonIndexRouteObject {
  children?: RouteObjectWithHandle[];
  handle?: BreadcrumbHandle;
}

type RouteObjectWithHandle =
  IndexRouteObjectWithHandle | NoIndexRouteObjectWithHandle;

/**
 * Route configuration
 *
 * Protection pattern:
 * - Parent routes with ProtectedRoute automatically protect all child routes
 * - Child routes only need ProtectedRoute if they have DIFFERENT `requires`
 * - Example: ApplicationList parent checks `read`, children inherit this
 *   protection
 * - Exception: Routes with different actions (e.g., `create`) need their
 *   own wrapper and `requires` definition
 */
export const routes: RouteObjectWithHandle[] = [
  {
    // Layout: Root
    children: [
      // Home (not required as long as we only have one module implemented)
      {
        element: <Navigate replace to="tenant-management" />,
        index: true,
      },

      {
        element: (
          <ProtectedRoute
            component={Organizations}
            requires={{
              actions: [GLOBAL_ACTIONS.ORGS_ACCESS],
              resource: GLOBAL_RESOURCES.APP,
            }}
          />
        ),
        handle: {
          breadcrumb: { translationKey: 'nav.organizations' },
        },
        path: 'organizations',
      },

      // Module: Tenant management
      {
        children: [
          {
            element: (
              <ProtectedRoute
                component={ApplicationLists}
                requires={{
                  actions: [GLOBAL_ACTIONS.TM_ACCESS],
                  resource: GLOBAL_RESOURCES.APP,
                }}
              />
            ),
            index: true,
          },

          {
            children: [
              {
                children: [
                  {
                    element: <Details />,
                    index: true,
                  },
                  {
                    element: <ApplicationListTenants />,
                    path: 'tenants',
                  },
                  {
                    element: <ApplicationListApplications />,
                    path: 'applications',
                  },
                ],
                element: (
                  <ProtectedRoute
                    component={ApplicationList}
                    requires={{
                      actions: [GLOBAL_ACTIONS.TM_ACCESS],
                      resource: GLOBAL_RESOURCES.APP,
                    }}
                  />
                ),
              },
              {
                children: [
                  {
                    element: <TenantDetails />,
                    index: true,
                  },
                  {
                    element: <Members />,
                    path: 'members',
                  },
                ],
                element: (
                  <ProtectedRoute
                    component={Tenant}
                    requires={{
                      actions: [GLOBAL_ACTIONS.TM_ACCESS],
                      resource: GLOBAL_RESOURCES.APP,
                    }}
                  />
                ),
                handle: {
                  breadcrumb: {
                    component: DynamicTenantBreadcrumb,
                  },
                },
                path: 'tenants/:tenantId',
              },
              {
                element: (
                  <ProtectedRoute
                    component={TenantCreate}
                    requires={{
                      actions: [GLOBAL_ACTIONS.TM_ACCESS],
                      resource: GLOBAL_RESOURCES.APP,
                    }}
                  />
                ),
                handle: {
                  breadcrumb: { translationKey: 'nav.newTenant' },
                },
                path: 'tenants/new',
              },
            ],
            handle: {
              breadcrumb: {
                component: DynamicApplicationListBreadcrumb,
              },
            },
            path: ':applicationListId',
          },
          {
            element: (
              <ProtectedRoute
                component={ApplicationListCreate}
                requires={{
                  actions: [GLOBAL_ACTIONS.TM_APPLICATION_LIST_CREATE],
                  resource: GLOBAL_RESOURCES.TM_APPLICATION_LIST,
                }}
              />
            ),
            handle: {
              breadcrumb: { translationKey: 'nav.newApplicationList' },
            },
            path: 'new',
          },
        ],
        handle: {
          breadcrumb: { translationKey: 'nav.tenantManagement' },
        },
        path: 'tenant-management',
      },

      // Module: Invitations
      {
        children: [
          {
            element: (
              <ProtectedRoute
                component={Invitations}
                requires={{
                  actions: [GLOBAL_ACTIONS.INVITATIONS_ACCESS],
                  resource: GLOBAL_RESOURCES.APP,
                }}
              />
            ),
            index: true,
          },
          {
            element: (
              <ProtectedRoute
                component={InvitationCreate}
                requires={{
                  actions: [GLOBAL_ACTIONS.INVITATIONS_ACCESS],
                  resource: GLOBAL_RESOURCES.APP,
                }}
              />
            ),
            handle: {
              breadcrumb: { translationKey: 'nav.newInvitation' },
            },
            path: 'new',
          },
          {
            element: (
              <ProtectedRoute
                component={Invitation}
                requires={{
                  actions: [GLOBAL_ACTIONS.INVITATIONS_ACCESS],
                  resource: GLOBAL_RESOURCES.APP,
                }}
              />
            ),
            handle: {
              breadcrumb: {
                component: DynamicInvitationBreadcrumb,
                preserveSearch: true,
              },
            },
            path: ':code',
          },
        ],
        handle: {
          breadcrumb: { translationKey: 'nav.invitations' },
        },
        loader: async () => {
          await i18n.loadNamespaces('invitations');
          return null;
        },
        path: 'invitations',
      },
    ],
    element: (
      <ProtectedRoute
        component={RootLayout}
        requires={{
          actions: [GLOBAL_ACTIONS.APP_ACCESS],
          resource: GLOBAL_RESOURCES.APP,
        }}
      />
    ),
    errorElement: <RootBoundary />,
    handle: {
      breadcrumb: { translationKey: 'nav.home' },
    },
    path: '/',
  },

  // Support routes
  {
    element: <Callback />,
    path: 'callback',
  },
];
